import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ARRANJOS, nomeDaEmpresa } from "@/components/finance/proposta/arranjo-de-faturamento";
import type { ArranjoFaturamento, OmieEmpresaChave } from "@/types/finance";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import { supabase } from "@/integrations/supabase/client";
import { formatPercent } from "@/utils/money";
import { useToast } from "@/hooks/use-toast";
import { AGENCIA_PORTES, type Agencia } from "@/types/agencia";
import { reportUserError } from "@/lib/report-error";

interface AgenciaEditModalProps {
  agencia: Agencia;
  onClose: () => void;
  onSuccess: () => void;
}

const PORTES_OPTIONS = AGENCIA_PORTES.map((porte) => ({
  label: porte,
  value: porte,
}));

const SEM_PADRAO = "sem_padrao";
const EMPRESAS_QUE_FATURAM: OmieEmpresaChave[] = ["corisco", "faisca", "gota_serena"];

/** Same as company_arrangement in the API. */
function arranjoUsualDaEmpresa(chave: OmieEmpresaChave): ArranjoFaturamento {
  return chave === "gota_serena" ? "repasse_liquido" : "taxa_e_comissao";
}

/** Número inteiro do campo; vazio ou fora do intervalo = null. */
function inteiroOuNulo(texto: string, minimo: number, maximo: number): number | null {
  const numero = Number(texto.trim());
  return texto.trim() !== "" && Number.isInteger(numero) && numero >= minimo && numero <= maximo ? numero : null;
}

export function AgenciaEditModal({ agencia, onClose, onSuccess }: AgenciaEditModalProps) {
  const [nome, setNome] = useState(agencia.nome);
  const [segmento, setSegmento] = useState(agencia.segmento || "");
  const [categorias, setCategorias] = useState<string[]>(agencia.categorias || []);
  const [email, setEmail] = useState(agencia.email || "");
  const [telefone, setTelefone] = useState(agencia.telefone || "");
  const [website, setWebsite] = useState(agencia.website || "");
  const [cidade, setCidade] = useState(agencia.cidade || "");
  const [bairro, setBairro] = useState(agencia.bairro || "");
  const [observacoes, setObservacoes] = useState(agencia.observacoes || "");
  const [logoPath, setLogoPath] = useState(agencia.logo_path || "");
  const [cor, setCor] = useState(agencia.cor || "");
  const [sigla, setSigla] = useState(agencia.sigla || "");
  const [razaoSocial, setRazaoSocial] = useState(agencia.razao_social || "");
  const [cnpj, setCnpj] = useState(agencia.cnpj || "");
  const [endereco, setEndereco] = useState(agencia.endereco || "");
  const [contatoNome, setContatoNome] = useState(agencia.contato_nome || "");
  const [contatoEmail, setContatoEmail] = useState(agencia.contato_email || "");
  const [contatoCargo, setContatoCargo] = useState(agencia.contato_cargo || "");
  const [faturaPorCompetenciaMensal, setFaturaPorCompetenciaMensal] = useState(
    agencia.fatura_por_competencia_mensal ?? false,
  );
  const [diaEmissaoCompetencia, setDiaEmissaoCompetencia] = useState(
    agencia.dia_emissao_competencia != null ? String(agencia.dia_emissao_competencia) : "",
  );
  const [prazoPagamentoCompetencia, setPrazoPagamentoCompetencia] = useState(
    agencia.prazo_pagamento_competencia_dias != null ? String(agencia.prazo_pagamento_competencia_dias) : "",
  );
  const [impostosPercentual, setImpostosPercentual] = useState(
    agencia.impostos_percentual ? formatPercent(agencia.impostos_percentual) : "",
  );
  const [faturamentoEmpresa, setFaturamentoEmpresa] = useState<OmieEmpresaChave | null>(
    agencia.faturamento_empresa ?? null,
  );
  const [faturamentoArranjo, setFaturamentoArranjo] = useState<ArranjoFaturamento | null>(
    agencia.faturamento_arranjo ?? null,
  );
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error } = await supabase
        .from("agencias")
        .update({
          nome,
          segmento,
          categorias: categorias.length > 0 ? categorias : null,
          email,
          telefone,
          website,
          cidade,
          bairro,
          observacoes,
          logo_path: logoPath || null,
          cor: cor || null,
          sigla: sigla || null,
          razao_social: razaoSocial || null,
          cnpj: cnpj || null,
          endereco: endereco || null,
          contato_nome: contatoNome || null,
          contato_email: contatoEmail || null,
          contato_cargo: contatoCargo || null,
          fatura_por_competencia_mensal: faturaPorCompetenciaMensal,
          dia_emissao_competencia: inteiroOuNulo(diaEmissaoCompetencia, 1, 31),
          prazo_pagamento_competencia_dias: inteiroOuNulo(prazoPagamentoCompetencia, 0, 365),
          impostos_percentual: Number(impostosPercentual.replace(",", ".")) || null,
          faturamento_empresa: faturamentoEmpresa,
          faturamento_arranjo: faturamentoEmpresa ? faturamentoArranjo : null,
        })
        .eq("id", agencia.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Agência atualizada com sucesso!",
      });

      onSuccess();
    } catch (error) {
      console.error("Erro ao atualizar agência:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a agência. " + reportUserError(error, { source: "agencia-edit-modal" }).description,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-[90vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Agência</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome da Agência *</Label>
            <Input
              id="nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="segmento">Segmento</Label>
              <Input
                id="segmento"
                value={segmento}
                onChange={(e) => setSegmento(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Portes</Label>
              <MultiSelect
                options={PORTES_OPTIONS}
                selected={categorias}
                onChange={setCategorias}
                placeholder="Selecione..."
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bairro">Bairro</Label>
              <Input
                id="bairro"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="logoPath">Logo (caminho do arquivo)</Label>
              <Input
                id="logoPath"
                value={logoPath}
                onChange={(e) => setLogoPath(e.target.value)}
                placeholder="Ex: /almap-logo.jpg.jpeg"
              />
              {logoPath && (
                <img
                  src={logoPath}
                  alt="Preview da logo"
                  className="h-10 max-w-[160px] object-contain mt-1"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cor">Cor da agência</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="cor"
                  value={cor || "#6d28d9"}
                  onChange={(e) => setCor(e.target.value)}
                  className="h-10 w-14 cursor-pointer rounded border border-input bg-background p-1"
                />
                <Input
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                  placeholder="#6d28d9"
                  className="flex-1"
                />
              </div>
              {cor && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-6 w-6 rounded" style={{ backgroundColor: cor }} />
                  <span className="text-xs text-muted-foreground">{cor}</span>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Dados para proposta</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sigla">Sigla</Label>
                <Input
                  id="sigla"
                  value={sigla}
                  onChange={(e) => setSigla(e.target.value)}
                  placeholder="Ex: MTO"
                />
                <p className="text-xs text-muted-foreground">
                  Usada no código da proposta, ex.: MTO
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="razaoSocial">Razão social</Label>
              <Input
                id="razaoSocial"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contatoNome">Contato</Label>
                <Input
                  id="contatoNome"
                  value={contatoNome}
                  onChange={(e) => setContatoNome(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contatoEmail">Email do contato</Label>
                <Input
                  id="contatoEmail"
                  type="email"
                  value={contatoEmail}
                  onChange={(e) => setContatoEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contatoCargo">Cargo do contato</Label>
                <Input
                  id="contatoCargo"
                  value={contatoCargo}
                  onChange={(e) => setContatoCargo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="faturaPorCompetenciaMensal">Fatura por competência mensal</Label>
                <p className="text-xs text-muted-foreground">
                  Ligado, o invite divide cada período em uma nota por mês. Cada proposta pode escolher diferente.
                </p>
              </div>
              <Switch
                id="faturaPorCompetenciaMensal"
                checked={faturaPorCompetenciaMensal}
                onCheckedChange={setFaturaPorCompetenciaMensal}
              />
            </div>
            {faturaPorCompetenciaMensal && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="diaEmissaoCompetencia">Dia de emissão da nota</Label>
                  <Input
                    id="diaEmissaoCompetencia"
                    inputMode="numeric"
                    placeholder="Ex.: 20"
                    value={diaEmissaoCompetencia}
                    onChange={(e) => setDiaEmissaoCompetencia(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Dia do mês seguinte ao trabalhado. Sugestão: cada proposta pode mudar.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prazoPagamentoCompetencia">Prazo de pagamento (dias)</Label>
                  <Input
                    id="prazoPagamentoCompetencia"
                    inputMode="numeric"
                    placeholder="Ex.: 18"
                    value={prazoPagamentoCompetencia}
                    onChange={(e) => setPrazoPagamentoCompetencia(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Dias entre a nota e o pagamento da agência. Sugestão: cada proposta pode mudar.
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="impostosPercentual">Impostos (%)</Label>
              <Input
                id="impostosPercentual"
                inputMode="decimal"
                placeholder="Sem impostos"
                value={impostosPercentual}
                onChange={(e) => setImpostosPercentual(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Sobre profissional + plataforma, à parte na proposta. Cada proposta pode mudar.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="faturamentoEmpresa">Quem fatura a agência</Label>
                <Select
                  value={faturamentoEmpresa ?? SEM_PADRAO}
                  onValueChange={(valor) => {
                    if (valor === SEM_PADRAO) {
                      setFaturamentoEmpresa(null);
                      setFaturamentoArranjo(null);
                      return;
                    }
                    setFaturamentoEmpresa(valor as OmieEmpresaChave);
                    setFaturamentoArranjo((atual) => atual ?? arranjoUsualDaEmpresa(valor as OmieEmpresaChave));
                  }}
                >
                  <SelectTrigger id="faturamentoEmpresa">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_PADRAO}>Sem padrão</SelectItem>
                    {EMPRESAS_QUE_FATURAM.map((chave) => (
                      <SelectItem key={chave} value={chave}>
                        {nomeDaEmpresa(chave)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {faturamentoEmpresa && (
                <div className="space-y-2">
                  <Label htmlFor="faturamentoArranjo">Como</Label>
                  <Select
                    value={faturamentoArranjo ?? undefined}
                    onValueChange={(valor) => setFaturamentoArranjo(valor as ArranjoFaturamento)}
                  >
                    <SelectTrigger id="faturamentoArranjo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARRANJOS.map((opcao) => (
                        <SelectItem key={opcao.valor} value={opcao.valor}>
                          {opcao.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {faturamentoEmpresa && faturamentoArranjo
                ? ARRANJOS.find((opcao) => opcao.valor === faturamentoArranjo)?.descricao(nomeDaEmpresa(faturamentoEmpresa))
                : "Sem padrão, a proposta nova repete a última proposta da agência."}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
            />
          </div>
          <Button type="submit" className="w-full">
            Salvar Alterações
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
